import moment from 'moment';
import { createSelector } from 'reselect';
import {
  selectIsAdminOfCurrentCourse,
  selectCurrentCourseSubscriptionId,
} from '../selectors';
import { selectIsFacilitatorInCurrentCourse } from '../../Enrolments/selectors';
import { selectCurrentUserEducatorStatus } from '../../Users/selectors';
import { selectCurrentUserSubscriptionPlanSummaries } from '../../UserSubscriptionPlans/selectors';

const getDaysRemaining = (expiryDate: string | null): number | null => {
  let daysRemaining = null;

  // expiryDate could be null if this is an unlimited trial
  if (expiryDate) {
    const momentDate = moment(expiryDate);
    if (momentDate && momentDate.isValid()) {
      const today = moment();
      daysRemaining = momentDate.diff(today, 'days');
    }
  }

  return daysRemaining;
};

export type CourseAlertBannerType =
  | 'educator-trial'
  | 'personal-trial'
  | 'payment-failure';

export type TrialBanner = {
  type: 'educator-trial' | 'personal-trial';
  daysRemaining: number;
};

export type PaymentFailureBanner = {
  type: 'payment-failure';
};

export type CourseAlertBanner = TrialBanner | PaymentFailureBanner;

export const selectCourseAlertBanners = createSelector(
  selectIsAdminOfCurrentCourse,
  selectIsFacilitatorInCurrentCourse,
  selectCurrentUserEducatorStatus,
  selectCurrentCourseSubscriptionId,
  selectCurrentUserSubscriptionPlanSummaries,
  (
    isAdmin,
    isStaff,
    educatorStatus,
    subscriptionId,
    subscriptionPlans
  ): Array<CourseAlertBanner> => {
    const banners: Array<CourseAlertBanner> = [];

    // only show these banners if we are inside a course for which the current user is an admin/staff member
    if (isAdmin || isStaff) {
      if (subscriptionId) {
        // this is a subscription course;
        // so show the subscription trial banner => if they're on a trial
        const personalTrial = subscriptionPlans.find(
          (subscription) =>
            subscription.type === 'personal' && subscription.status === 'trial'
        );

        if (personalTrial) {
          const daysRemaining = getDaysRemaining(personalTrial.expiryDate);
          if (daysRemaining !== null) {
            banners.push({
              type: 'personal-trial',
              daysRemaining: Math.max(daysRemaining, 0),
              // suffers from the same issue as the educator trials due to
              // trials being marked as expired dependent on running of the daily task
            });
          }
        }
        // else it's a course under the legacy "educator" system
        // so show the "educator" trial banner; if they're on a trial
      } else if (educatorStatus === 'trial') {
        // Due to the timing of the daily task that expires user subscriptions;
        // sometimes it is possible for the expiry date to have lapsed, but the
        // current user's educator status is still 'trial'... in this case,
        // we want to continue displaying the trial banner
        // Therefore, always display the trial banner if the educatorStatus = trial
        // regardless of the expiry date

        // but don't show it until we have loaded the user subscription plan
        // and calculated the daysRemaining, otherwise the banner will flash
        // with the 'about to expire' message before updating with 'N days left'
        const educatorSubscription = subscriptionPlans.find(
          (plan) => plan.type === 'educator'
        );
        if (educatorSubscription) {
          const daysRemaining = getDaysRemaining(
            educatorSubscription.expiryDate
          );
          if (daysRemaining !== null) {
            banners.push({
              type: 'educator-trial',
              daysRemaining: Math.max(daysRemaining, 0),
            });
          }
        }
      }

      const overdueSubscription = subscriptionPlans.find(
        (plan) => plan.status === 'past_due'
      );
      if (overdueSubscription) {
        banners.push({
          type: 'payment-failure',
        });
      }
    }
    return banners;
  }
);
